// Third-party
import React from 'react';

// Components
import Question from './Question';
import { ActionBox } from '../misc';
import { PrimaryButton } from '../buttons';

// Util
import { FormattedMessage, formatMessage, formatLocalizedString } from '../../../utils/i18n';
import { QUESTIONNAIRE_TYPES } from '../../../utils/questionnaire';
import QuestionnaireSectionItem from '@/types/questionnaire-section-item';
import Locale from '@/types/locale';
import QuestionOptionItem from '@/types/question-option-item';
import SavedSections from '@/types/saved-sections';
import QuestionItem from '@/types/question-item';

type Props = {
  questionnaireType: QUESTIONNAIRE_TYPES;
  section: QuestionnaireSectionItem;
  isLastSection: boolean;
  showSubtotal: boolean;
  goNextSection: (height?: number) => void;
  locale: Locale;
  saveAnswers: (sectionId: string, questionId: string, option: QuestionOptionItem, text?: string) => void;
  clearAnswers?: (sectionId: string, questionId: string) => void;
  saveNotes?: (sectionId: string, questionId: string, notes?: string) => void;
  clearNotes?: (sectionId: string, questionId: string) => void;
  getSectionSubtotal: () => number;
  showErrors?: boolean;
  savedSections?: SavedSections;
};

class QuestionnaireSection extends React.Component<Props> {
  getSortedQuestions = () => {
    const { questions } = this.props.section;

    return questions.sort((a, b) => a.order - b.order);
  };

  continueSection = () => {
    this.props.goNextSection();
  };

  questionIsAnswered = (question: QuestionItem) => {
    const { savedSections, section } = this.props;

    if (question.optional) {
      return true;
    }

    return savedSections !== undefined && section._id in savedSections && question._id in savedSections[section._id].questions;
  };

  renderQuestions = () => {
    const { section, locale, showErrors, savedSections, saveAnswers, clearAnswers, saveNotes, clearNotes } = this.props;
    const sortedQuestions = this.getSortedQuestions();

    return sortedQuestions.map(question => (
      <Question
        key={question._id}
        question={question}
        showError={showErrors && !this.questionIsAnswered(question)}
        questionId={question._id}
        locale={locale}
        sectionId={section._id}
        saveAnswers={saveAnswers}
        clearAnswers={clearAnswers}
        saveNotes={saveNotes}
        clearNotes={clearNotes}
        savedSections={savedSections}
      />
    ));
  };

  getActionI18nKey = () => {
    if (!this.props.isLastSection) {
      return 'ContinueSection';
    }

    switch (this.props.questionnaireType) {
      case QUESTIONNAIRE_TYPES.RISK_PROFILE:
        return 'RiskProfile.Complete';
      case QUESTIONNAIRE_TYPES.RATIONALE:
      default:
        return 'Rationale.Complete';
    }
  };

  render() {
    const { section, showSubtotal, getSectionSubtotal, locale, isLastSection } = this.props;

    const actionI18nKey = this.getActionI18nKey();

    return (
      <div className="questionnaire-section" role="tabpanel" aria-labelledby={`section-${section._id}`} id={`section-panel-${section._id}`}>
        <div className="questions-container">{this.renderQuestions()}</div>

        {showSubtotal && (
          <div className="section-subtotal">
            <h1 className="flex-grow-1">
              <FormattedMessage id="Comparison.Questionnaire.Section.Subtotal" />
              {formatLocalizedString(section.title, locale)}
            </h1>

            <div className="subtotal-box">{getSectionSubtotal()}</div>
          </div>
        )}

        <ActionBox
          title={formatMessage(`Comparison.Questionnaire.${actionI18nKey}.Title`)}
          body={formatMessage(`Comparison.Questionnaire.${actionI18nKey}.Text`)}
        >
          <PrimaryButton onClick={this.continueSection}>
            <FormattedMessage id={isLastSection ? `Comparison.Questionnaire.${actionI18nKey}.Button` : 'Common.Continue'} />
          </PrimaryButton>
        </ActionBox>
      </div>
    );
  }
}

export default QuestionnaireSection;
